import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MangaNavbar from "@/components/feature/MangaNavbar";
import MangaFooter from "@/components/feature/MangaFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/MangaBreadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { comics } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);
  const [collected, setCollected] = useState(false);

  const chapters = useMemo(() => {
    const total = comic?.chapters ?? 0;
    const count = Math.min(12, total);
    return Array.from({ length: count }, (_, i) => {
      const num = total - count + 1 + i;
      return { num, label: `第${num}话` };
    }).reverse();
  }, [comic]);

  const similar = useMemo(
    () => (comic ? comics.filter((c) => c.genre === comic.genre && c.id !== comic.id).slice(0, 4) : []),
    [comic],
  );

  useSeo({
    title: comic
      ? `${comic.title} - ${comic.latestChapter}在线追更 | 天天漫画网`
      : "漫画详情 - 天天漫画网",
    description: comic
      ? `${comic.title}是${comic.genre}类${comic.status}连载漫画，已更新至${comic.latestChapter}，评分${comic.rating}。天天漫画网每日更新最新话，${comic.tags.join("、")}题材，追更不迷路。`
      : "天天漫画网漫画详情页。",
    keywords: comic
      ? `${comic.title},${comic.genre}漫画,${comic.status},漫画最新更新,追更漫画`
      : "漫画详情,天天漫画网",
    canonicalPath: `/comic/${id}`,
  });

  if (!comic) {
    return (
      <>
        <MangaNavbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-24 text-center">
          <i className="ri-ghost-2-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-2xl text-foreground-900">没有找到这部漫画</h1>
          <Link to="/" className="inline-flex items-center gap-1 mt-4 text-primary-600 hover:text-primary-700">
            <i className="ri-arrow-left-line"></i>返回首页
          </Link>
        </main>
        <MangaFooter />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", to: "/" },
    { label: comic.genre, to: "/latest" },
    { label: comic.title },
  ];

  const comicData = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: comic.title,
    author: { "@type": "Person", name: comic.author },
    genre: comic.genre,
    inLanguage: "zh-CN",
    description: comic.description,
    aggregateRating: { "@type": "AggregateRating", ratingValue: comic.rating, bestRating: 10, worstRating: 1 },
    url: `https://panelpeek.github.io/comic/${comic.id}`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: b.to ? `https://panelpeek.github.io${b.to}` : undefined,
      })),
    },
  };

  return (
    <>
      <JsonLd id="comic-jsonld" data={comicData} />
      <MangaNavbar />
      <Breadcrumb items={breadcrumbItems} />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <section className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 md:gap-10">
          <div className="lg:sticky lg:top-24 self-start">
            <div className="rounded-2xl overflow-hidden aspect-[3/4] bg-background-100 shadow-soft">
              <img
                src={comic.cover}
                alt={`${comic.title} - ${comic.genre}连载漫画封面`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {comic.tags.map((tag) => (
                <span key={tag} className="text-xs text-secondary-700 bg-secondary-100 px-2.5 py-1 rounded-md font-medium">
                  {tag}
                </span>
              ))}
              <span className="text-xs text-accent-700 bg-accent-100 px-2.5 py-1 rounded-md font-medium">{comic.status}</span>
            </div>

            <h1 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950">{comic.title}</h1>
            <p className="mt-2 text-sm text-foreground-500">
              {comic.genre} · 作者：{comic.author}
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-2xl bg-background-100 border border-background-200 p-3 text-center">
                <div className="font-heading text-lg text-accent-600">{comic.rating}</div>
                <div className="text-xs text-foreground-500">评分</div>
              </div>
              <div className="rounded-2xl bg-background-100 border border-background-200 p-3 text-center">
                <div className="font-heading text-lg text-foreground-900">{comic.chapters}</div>
                <div className="text-xs text-foreground-500">已更新话数</div>
              </div>
              <div className="rounded-2xl bg-background-100 border border-background-200 p-3 text-center">
                <div className="font-heading text-lg text-primary-600">{comic.viewCount}</div>
                <div className="text-xs text-foreground-500">人气</div>
              </div>
              <div className="rounded-2xl bg-background-100 border border-background-200 p-3 text-center">
                <div className="font-heading text-lg text-secondary-600">{comic.genre}</div>
                <div className="text-xs text-foreground-500">分类</div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setCollected((v) => !v)}
                className={`inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  collected
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={collected ? "ri-check-double-line" : "ri-bookmark-line"}></i>
                {collected ? "已追更" : "收藏追更"}
              </button>
              <a
                href="#chapters"
                className="inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors"
              >
                直接看最新话 <i className="ri-arrow-down-line"></i>
              </a>
            </div>

            <h2 className="mt-8 text-sm font-medium text-foreground-400 uppercase tracking-wide">作品简介</h2>
            <p className="mt-2 text-sm md:text-base text-foreground-700 leading-7">{comic.description}</p>

            <h2 id="chapters" className="mt-8 text-sm font-medium text-foreground-400 uppercase tracking-wide scroll-mt-24">
              最近更新
            </h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {chapters.map((ch) => (
                <a
                  key={ch.num}
                  href="#chapters"
                  className="flex items-center justify-between rounded-xl bg-background-100 border border-background-200 px-4 py-3 text-sm text-foreground-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                >
                  {ch.label}
                  <i className="ri-play-circle-line text-foreground-300"></i>
                </a>
              ))}
            </div>
            <p className="mt-3 text-xs text-foreground-400">更多历史章节可前往完整目录查看 · 每日持续更新</p>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">同类作品推荐</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {similar.map((c) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group rounded-2xl bg-background-50 border border-background-200 overflow-hidden hover:border-primary-200 transition-colors"
              >
                <div className="aspect-[3/4] overflow-hidden bg-background-100">
                  <img
                    src={c.cover}
                    alt={`${c.title} - 同类${comic.genre}漫画推荐`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm md:text-base font-medium text-foreground-900 truncate">{c.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500 truncate">{c.latestChapter}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <MangaFooter />
      <BackToTop />
    </>
  );
}